import { useState } from 'react';
import { Dialog, HelpTip, KeywordPicker, NEGATIVE_KEYWORD_LIBRARY, PANEL_HELP, SubjectPhotoUploader, api, positiveSuggestions, useToast } from './_barrel.js';


        function EditSubjectDialog({ token, subject, onClose, onSuccess }) {
            const [form, setForm] = useState({
                subject_type_code:    subject.subject_type_code || 'principal',
                full_name:            subject.full_name || '',
                display_name:         subject.display_name || '',
                family_name:          subject.family_name || '',
                year_of_birth:        subject.year_of_birth ?? '',
                occupation:           subject.occupation || '',
                current_employer:     subject.current_employer || '',
                known_locations:      (subject.known_locations || []).join(', '),
                positive_keywords:    (subject.positive_keywords || []).join(', '),
                negative_keywords:    (subject.negative_keywords || []).join(', '),
                rss_feeds:            (subject.rss_feeds || []).join('\n'),
                lawful_basis:         subject.lawful_basis || 'legitimate_interests',
                processing_purpose:   subject.processing_purpose || '',
                lia_reference:        subject.lia_reference || '',
                client_tag:           subject.client_tag || '',
                background_notes:     subject.background_notes || '',
            });
            const [aliases, setAliases] = useState(subject.aliases || []);
            const [newAlias, setNewAlias] = useState('');
            const [handles, setHandles] = useState(subject.handles || []);
            const [newHandle, setNewHandle] = useState({ platform: 'twitter', handle: '', url: '' });
            const [submitting, setSubmitting] = useState(false);
            const [error, setError] = useState(null);
            const toast = useToast();

            const splitList = (s) =>
                (s || '')
                    .split(',')
                    .map(x => x.trim())
                    .filter(Boolean);

            const submit = async (e) => {
                e.preventDefault();
                setSubmitting(true);
                setError(null);
                try {
                    const body = {
                        subject_type_code:  form.subject_type_code,
                        full_name:          form.full_name,
                        display_name:       form.display_name || null,
                        family_name:        form.family_name || null,
                        year_of_birth:      form.year_of_birth ? parseInt(form.year_of_birth, 10) : null,
                        occupation:         form.occupation || null,
                        current_employer:   form.current_employer || null,
                        known_locations:    splitList(form.known_locations),
                        positive_keywords:  splitList(form.positive_keywords),
                        negative_keywords:  splitList(form.negative_keywords),
                        rss_feeds:          (form.rss_feeds || '').split(/[\n,]/).map(x => x.trim()).filter(Boolean),
                        lawful_basis:       form.lawful_basis,
                        processing_purpose: form.processing_purpose,
                        lia_reference:      form.lia_reference || null,
                        client_tag:         form.client_tag || null,
                        background_notes:   form.background_notes || null,
                    };
                    await api(`/api/v1/subjects/${subject.id}`, { method: 'PATCH', body: JSON.stringify(body) }, token);
                    toast('Subject updated');
                    onSuccess?.();
                    onClose();
                } catch (err) { setError(err.message); }
                finally { setSubmitting(false); }
            };

            // Aliases — add/remove inline via the API
            const addAlias = async () => {
                if (!newAlias.trim()) return;
                try {
                    const res = await api(`/api/v1/subjects/${subject.id}/aliases`,
                        { method: 'POST', body: JSON.stringify({ alias: newAlias.trim() }) }, token);
                    setAliases([...aliases, res]);
                    setNewAlias('');
                } catch (err) { setError(err.message); }
            };
            const removeAlias = async (aliasId) => {
                try {
                    await api(`/api/v1/subjects/${subject.id}/aliases/${aliasId}`, { method: 'DELETE' }, token);
                    setAliases(aliases.filter(a => a.id !== aliasId));
                } catch (err) { setError(err.message); }
            };

            // Handles — same pattern
            const addHandle = async () => {
                if (!newHandle.handle.trim()) return;
                try {
                    const body = {
                        platform: newHandle.platform,
                        handle: newHandle.handle.trim(),
                        url: newHandle.url.trim() || null,
                    };
                    const res = await api(`/api/v1/subjects/${subject.id}/handles`,
                        { method: 'POST', body: JSON.stringify(body) }, token);
                    setHandles([...handles, res]);
                    setNewHandle({ platform: 'twitter', handle: '', url: '' });
                } catch (err) { setError(err.message); }
            };
            const removeHandle = async (handleId) => {
                try {
                    await api(`/api/v1/subjects/${subject.id}/handles/${handleId}`, { method: 'DELETE' }, token);
                    setHandles(handles.filter(h => h.id !== handleId));
                } catch (err) { setError(err.message); }
            };

            return (
                <Dialog onClose={onClose} title={`Edit Subject — ${subject.display_name || subject.full_name}`} wide>
                    <form onSubmit={submit} className="space-y-5">
                        {/* Subject Photo */}
                        <SubjectPhotoUploader subject={subject} token={token} />

                        {/* Identity */}
                        <div className="space-y-3">
                            <div className="font-mono text-xs uppercase tracking-widest gold">Identity</div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label>Subject Type</label>
                                    <select value={form.subject_type_code} onChange={e => setForm({...form, subject_type_code: e.target.value})}>
                                        <option value="principal">Principal</option>
                                        <option value="family_member">Family member</option>
                                        <option value="presenter">Presenter</option>
                                        <option value="executive">Executive</option>
                                        <option value="public_figure">Public figure</option>
                                        <option value="staff">Staff</option>
                                        <option value="child">Child (minor)</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label>Year of Birth</label>
                                    <input type="number" min="1900" max="2100" value={form.year_of_birth}
                                        onChange={e => setForm({...form, year_of_birth: e.target.value})}
                                        placeholder="e.g. 1985" />
                                </div>
                                <div>
                                    <label>Full Name *</label>
                                    <input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required />
                                </div>
                                <div>
                                    <label>Display Name</label>
                                    <input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} />
                                </div>
                                <div>
                                    <label>Family Name (Surname)</label>
                                    <input value={form.family_name} onChange={e => setForm({...form, family_name: e.target.value})}
                                        placeholder="e.g. Carter" />
                                </div>
                                <div>
                                    <label>Occupation</label>
                                    <input value={form.occupation} onChange={e => setForm({...form, occupation: e.target.value})}
                                        placeholder="e.g. broadcast presenter" />
                                </div>
                            </div>
                        </div>

                        {/* Disambiguators */}
                        <div className="space-y-3">
                            <div className="font-mono text-xs uppercase tracking-widest gold">Disambiguators</div>
                            <div className="text-xs text-muted">
                                These fields narrow online searches so we don't return hits for unrelated people with the same name. Without at least one positive disambiguator, a single-word name will not be searched.
                            </div>
                            <div>
                                <label>Current Employer</label>
                                <input value={form.current_employer} onChange={e => setForm({...form, current_employer: e.target.value})}
                                    placeholder="e.g. Carter Family Office" />
                            </div>
                            <div>
                                <label>Known Locations (comma-separated)</label>
                                <input value={form.known_locations} onChange={e => setForm({...form, known_locations: e.target.value})}
                                    placeholder="e.g. London, Surrey, Monaco" />
                            </div>
                            <div>
                                <KeywordPicker
                                    label="Positive Keywords"
                                    value={form.positive_keywords}
                                    onChange={(v) => setForm({...form, positive_keywords: v})}
                                    suggestions={positiveSuggestions(form)}
                                    helpText="Terms that confirm a search hit is THIS subject. Click suggestions or type your own."
                                    placeholder="Type, comma to add"
                                />
                            </div>
                            <div>
                                <KeywordPicker
                                    label="Negative Keywords"
                                    value={form.negative_keywords}
                                    onChange={(v) => setForm({...form, negative_keywords: v})}
                                    suggestions={NEGATIVE_KEYWORD_LIBRARY}
                                    helpText="Terms that rule out a search hit. Click any that apply, or type your own."
                                    placeholder="Type, comma to add"
                                />
                            </div>
                            <div>
                                <label>RSS / News Feeds (one URL per line)</label>
                                <textarea value={form.rss_feeds} onChange={e => setForm({...form, rss_feeds: e.target.value})}
                                    rows={3} style={{width:'100%', fontFamily:'IBM Plex Mono, monospace', fontSize:'12px'}}
                                    placeholder={"https://example.com/feed.xml\nhttps://localpaper.co.uk/rss"} />
                                <div className="text-xs text-muted mt-1">Analyst-curated RSS/Atom feeds polled with the other collectors and classified the same way — a local paper, a niche blog, a court-listing feed.</div>
                            </div>
                        </div>

                        {/* Aliases */}
                        <div className="space-y-2">
                            <div className="font-mono text-xs uppercase tracking-widest gold">Aliases (Other Names)</div>
                            <div className="space-y-1">
                                {aliases.length === 0 && <div className="text-xs text-muted">No aliases recorded.</div>}
                                {aliases.map(a => (
                                    <div key={a.id} className="flex justify-between items-center py-1 border-b border-veil">
                                        <span className="text-sm">{a.alias}</span>
                                        <button type="button" className="text-xs text-muted hover:text-[var(--red)] uppercase tracking-widest"
                                            onClick={() => removeAlias(a.id)}>Remove</button>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <input value={newAlias} onChange={e => setNewAlias(e.target.value)}
                                    placeholder="e.g. Maddie Carter" style={{flex: 1}} />
                                <button type="button" className="btn-ghost" onClick={addAlias}>+ Add Alias</button>
                            </div>
                        </div>

                        {/* Handles */}
                        <div className="space-y-2">
                            <div className="font-mono text-xs uppercase tracking-widest gold">Online Handles<HelpTip text={PANEL_HELP.online_handles} /></div>
                            <div className="space-y-1">
                                {handles.length === 0 && <div className="text-xs text-muted">No online handles recorded.</div>}
                                {handles.map(h => (
                                    <div key={h.id} className="flex justify-between items-center py-1 border-b border-veil">
                                        <span className="text-sm">
                                            <span className="text-muted font-mono text-xs uppercase mr-2">{h.platform}</span>
                                            {h.handle}
                                            {h.url && <a href={h.url} target="_blank" rel="noopener noreferrer" className="ml-2 text-xs text-muted hover:text-[var(--gold)]">↗</a>}
                                        </span>
                                        <button type="button" className="text-xs text-muted hover:text-[var(--red)] uppercase tracking-widest"
                                            onClick={() => removeHandle(h.id)}>Remove</button>
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-12 gap-2">
                                <select className="col-span-3" value={newHandle.platform}
                                    onChange={e => setNewHandle({...newHandle, platform: e.target.value})}>
                                    <option value="twitter">X / Twitter</option>
                                    <option value="instagram">Instagram</option>
                                    <option value="tiktok">TikTok</option>
                                    <option value="facebook">Facebook</option>
                                    <option value="linkedin">LinkedIn</option>
                                    <option value="youtube">YouTube</option>
                                    <option value="reddit">Reddit</option>
                                    <option value="telegram">Telegram</option>
                                    <option value="threads">Threads</option>
                                    <option value="bluesky">Bluesky</option>
                                    <option value="other">Other</option>
                                </select>
                                <input className="col-span-4" value={newHandle.handle}
                                    onChange={e => setNewHandle({...newHandle, handle: e.target.value})}
                                    placeholder="handle (no @)" />
                                <input className="col-span-4" value={newHandle.url}
                                    onChange={e => setNewHandle({...newHandle, url: e.target.value})}
                                    placeholder="profile URL (optional)" />
                                <button type="button" className="btn-ghost col-span-1" onClick={addHandle}>+</button>
                            </div>
                        </div>

                        {/* Lawful Basis */}
                        <div className="space-y-3">
                            <div className="font-mono text-xs uppercase tracking-widest gold">Processing Basis</div>
                            <div>
                                <label>Lawful Basis</label>
                                <select value={form.lawful_basis} onChange={e => setForm({...form, lawful_basis: e.target.value})}>
                                    <option value="legitimate_interests">Legitimate interests</option>
                                    <option value="consent">Consent</option>
                                    <option value="contract">Contract</option>
                                    <option value="legal_obligation">Legal obligation</option>
                                    <option value="vital_interests">Vital interests</option>
                                    <option value="public_task">Public task</option>
                                </select>
                            </div>
                            <div>
                                <label>Processing Purpose *</label>
                                <textarea rows="2" value={form.processing_purpose}
                                    onChange={e => setForm({...form, processing_purpose: e.target.value})} required />
                            </div>
                            <div>
                                <label>LIA Reference</label>
                                <input value={form.lia_reference} onChange={e => setForm({...form, lia_reference: e.target.value})}
                                    placeholder="LIA-2026-XXX" />
                            </div>
                            <div>
                                <label>Client / Engagement (optional)</label>
                                <input value={form.client_tag} onChange={e => setForm({...form, client_tag: e.target.value})}
                                    placeholder="e.g. Purple Protection — A1 family 2026" />
                            </div>
                        </div>

                        {/* Background notes */}
                        <div>
                            <label>Background Notes (Free Text)</label>
                            <textarea rows="3" value={form.background_notes}
                                onChange={e => setForm({...form, background_notes: e.target.value})}
                                placeholder="Anything useful that doesn't fit the structured fields — previous employers, family context, prior addresses if relevant."></textarea>
                        </div>

                        {error && <div className="text-[var(--red)] text-sm">{error}</div>}
                        <div className="flex justify-end gap-3 pt-2">
                            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                            <button type="submit" className="btn-gold" disabled={submitting}>
                                {submitting ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </Dialog>
            );
        }

export { EditSubjectDialog };
